import { useRef, useState } from 'react';

import { Button } from '@design-system';
import { AuthSeparator } from '@client/components';

import { PartnerConnectionForm } from './partner-connection-form';
import { PartnerShareConsentDialog } from './partner-share-consent-dialog';
import { PartnerLegacyAttestationDialog } from './partner-legacy-attestation-dialog';
import { PartnerDisconnectDialog } from './partner-disconnect-dialog';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import {
  useDistributionPartnerConnection,
  type DistributionContactWithPartner,
} from './use-distribution-partner-connection';

function contactDisplayName(contact: DistributionContactWithPartner): string {
  const name = `${contact.advisorFirstName ?? ''} ${contact.advisorLastName ?? ''}`.trim();

  return name || contact.advisorEmail;
}

interface PartnerConnectionSectionProps {
  variant: 'onboarding' | 'settings';
  onConnected?: () => void;
}

export function PartnerConnectionSection({ variant, onConnected }: PartnerConnectionSectionProps) {
  // EDJ advisors don't share legacy (pre-cutoff) clients — they only auto-share going forward.
  const isEdjPilot = useIsEdjPilotAdvisor();
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [legacyDialogOpen, setLegacyDialogOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<DistributionContactWithPartner | null>(
    null,
  );
  // Read inside the connect-success callback without re-creating it on every change.
  const hasLegacyClientsRef = useRef(false);

  const connection = useDistributionPartnerConnection({
    onConnected: () => {
      setConsentDialogOpen(false);
      // After connecting, if the advisor has pre-new-consent clients, offer the legacy attestation.
      if (hasLegacyClientsRef.current) {
        setLegacyDialogOpen(true);
      }
      onConnected?.();
    },
  });

  // Only offer the legacy attestation if the advisor hasn't already shared their earlier clients.
  hasLegacyClientsRef.current =
    !isEdjPilot && connection.hasLegacyClients && !connection.hasLegacyShareConsent;

  const dialogs = (
    <>
      <PartnerShareConsentDialog
        open={consentDialogOpen}
        defaultChecked={connection.shareConsent}
        isConnecting={connection.isConnecting}
        onConfirm={autoShare => connection.connect(autoShare)}
        onCancel={() => setConsentDialogOpen(false)}
      />
      <PartnerLegacyAttestationDialog
        open={legacyDialogOpen}
        isSharing={connection.isSharingLegacy}
        onConfirm={() => connection.confirmLegacyShare(() => setLegacyDialogOpen(false))}
        onCancel={() => setLegacyDialogOpen(false)}
      />
      <PartnerDisconnectDialog
        open={disconnectTarget !== null}
        contactName={disconnectTarget ? contactDisplayName(disconnectTarget) : ''}
        isDisconnecting={connection.isDisconnecting}
        onConfirm={scope => {
          if (!disconnectTarget) return;

          connection.disconnect(
            disconnectTarget.partnerKey,
            disconnectTarget.advisorId,
            scope,
            () => setDisconnectTarget(null),
          );
        }}
        onCancel={() => setDisconnectTarget(null)}
      />
    </>
  );

  const form = (
    <>
      <PartnerConnectionForm
        partners={connection.partners}
        isLoadingPartners={connection.isLoadingPartners}
        contacts={connection.contacts}
        isLoadingContacts={connection.isLoadingContacts}
        selectedPartnerKeys={connection.selectedPartnerKeys}
        selectedContactIds={connection.selectedContactIds}
        onTogglePartner={connection.togglePartner}
        onToggleContact={connection.toggleContact}
        onToggleContacts={connection.toggleContacts}
        onConnect={() => setConsentDialogOpen(true)}
        isConnecting={connection.isConnecting}
        onRequestOther={connection.requestOther}
        isRequestingOther={connection.isRequestingOther}
        onRequestDisconnect={setDisconnectTarget}
        idPrefix={`${variant}-`}
      />
      {dialogs}
    </>
  );

  if (variant === 'onboarding') {
    return (
      <div className="flex flex-col items-center justify-center px-4 sm:px-0">
        <div className="w-full max-w-2xl space-y-6 sm:space-y-8">
          <div className="text-center">
            <h2 className="text-balance text-xl font-semibold text-gray-950 sm:text-3xl">
              Connect your distribution partners
            </h2>
            <p className="text-foreground mt-2 text-balance text-sm sm:text-base">
              Select the partners and the specific people you work with. Once connected, they can
              view the clients you share with them. Keep automatic sharing on to share every client
              you invite going forward, or turn it off to share clients one at a time from your
              Clients table.
            </p>
          </div>
          {form}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
      <AuthSeparator
        text="Distribution Partners"
        className="sm:col-span-6"
      />
      <p className="text-foreground text-sm sm:col-span-6">
        Connect the distribution partners you work with. Once connected, you can collaborate with
        the specific people you work with at each firm.
      </p>
      <div className="sm:col-span-6">{form}</div>

      {!isEdjPilot &&
        connection.hasLegacyClients &&
        connection.hasConnections &&
        !connection.hasLegacyShareConsent && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-4 sm:col-span-6">
          <div className="max-w-xl">
            <p className="text-foreground text-sm font-medium">Share your earlier clients</p>
            <p className="text-muted-foreground text-sm">
              Clients who joined before June 17, 2026 aren't shared automatically. Confirm you have
              their consent to share them with your distribution partner(s).
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setLegacyDialogOpen(true)}
          >
            Share earlier clients
          </Button>
        </div>
      )}
    </div>
  );
}
